// Copyright (c) Ping. All rights reserved.

import type { CallDirection, CallHistoryFilter, CallKind } from './types';

/**
 * User-facing copy for the call-history screen.
 *
 * Keys follow the `ping_callhistory_*` prefix coordinated with iOS-E
 * and AND-D under the Wave-2 naming contract.
 */
export const callHistoryStrings = {
  /** ping_callhistory_screenTitle */
  screenTitle: 'Call History',
  /** ping_callhistory_favoritesTitle */
  favoritesTitle: 'Favorites',
  /** ping_callhistory_searchPlaceholder */
  searchPlaceholder: 'Search calls',
  /** ping_callhistory_filterLabel */
  filterLabel: 'Filter',
  /** ping_callhistory_filter_<value> */
  filter: (filter: CallHistoryFilter): string => {
    switch (filter) {
      case 'all':
        return 'All';
      case 'missed':
        return 'Missed';
      case 'favorites':
        return 'Favorites';
    }
  },
  /** ping_callhistory_direction_<value> */
  direction: (direction: CallDirection): string => {
    switch (direction) {
      case 'incoming':
        return 'Incoming';
      case 'outgoing':
        return 'Outgoing';
      case 'missed':
        return 'Missed';
    }
  },
  /** ping_callhistory_kind_<value> */
  kind: (kind: CallKind): string => {
    switch (kind) {
      case 'audio':
        return 'Audio call';
      case 'video':
        return 'Video call';
      case 'group':
        return 'Group call';
    }
  },
  /** ping_callhistory_emptyTitle */
  emptyTitle: (filter: CallHistoryFilter, hasQuery: boolean): string => {
    if (hasQuery) {
      return 'No calls match your search';
    }
    switch (filter) {
      case 'all':
        return 'No call history yet';
      case 'missed':
        return 'No missed calls';
      case 'favorites':
        return 'No favorites yet';
    }
  },
  /** ping_callhistory_emptyBody */
  emptyBody: 'When you place or receive calls, they’ll appear here.',
  /** ping_callhistory_errorTitle */
  errorTitle: 'Couldn’t load your call history',
  /** ping_callhistory_errorRetry */
  errorRetry: 'Try again',
  /** ping_callhistory_actionCallBack */
  actionCallBack: 'Call back',
  /** ping_callhistory_actionMessage */
  actionMessage: 'Message',
  /** ping_callhistory_actionViewProfile */
  actionViewProfile: 'View profile',
  /** ping_callhistory_actionAddToFavorites */
  actionAddToFavorites: 'Add to favorites',
  /** ping_callhistory_actionRemoveFromFavorites */
  actionRemoveFromFavorites: 'Remove from favorites',
  /** ping_callhistory_actionDelete */
  actionDelete: 'Delete entry',
  /** ping_callhistory_detailsTitle */
  detailsTitle: 'Call details',
  /** ping_callhistory_detailsParticipants */
  detailsParticipants: 'Participants',
  /** ping_callhistory_detailsStarted */
  detailsStarted: 'Started',
  /** ping_callhistory_detailsDuration */
  detailsDuration: 'Duration',
  /** ping_callhistory_detailsKind */
  detailsKind: 'Type',
  /** ping_callhistory_detailsDirection */
  detailsDirection: 'Direction',
  /** ping_callhistory_addFavoritesTitle */
  addFavoritesTitle: 'Add to favorites',
  /** ping_callhistory_addFavoritesBody */
  addFavoritesBody: 'Pick the people you want to keep in your favorites list.',
  /** ping_callhistory_addFavoritesEmpty */
  addFavoritesEmpty: 'No one to favorite from this call.',
  /** ping_callhistory_addFavoritesConfirm */
  addFavoritesConfirm: 'Add',
  /** ping_callhistory_addFavoritesCancel */
  addFavoritesCancel: 'Cancel',
  /** ping_callhistory_close */
  close: 'Close',
  /** ping_callhistory_durationFormatHms */
  formatDuration: (seconds: number | null): string => {
    if (seconds == null || seconds < 1) {
      return '–';
    }
    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
      return `${h}:${pad2(m)}:${pad2(s)}`;
    }
    return `${m}:${pad2(s)}`;
  },
} as const;

export type CallHistoryStrings = typeof callHistoryStrings;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
